/**
 * Display helpers for application windows.
 *
 * Whether a program is accepting applications is decided on the server and
 * arrives on the current term as `applicationWindows` (one verdict per
 * program, for the signed-in person). Nothing here decides whether a program
 * accepts; these functions turn the verdict into words, dates, day counts and
 * badge colours, counting days by the server's own day rule.
 */

export const PROGRAM_WINDOW_ORDER = ['si', 'ta', 'la'];

export const PROGRAM_NAMES = {
    si: 'Supplemental Instruction',
    ta: 'Teaching Assistant',
    la: 'Learning Assistant',
};

const DAY_MS = 24 * 60 * 60 * 1000;
const universityDateFormat = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Denver',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

// Days since the epoch of the calendar date an instant falls on in Denver.
function universityDay(instant) {
    const parts = {};
    for (const { type, value } of universityDateFormat.formatToParts(instant)) {
        parts[type] = Number(value);
    }
    return Date.UTC(parts.year, parts.month - 1, parts.day) / DAY_MS;
}

// Same day rule as the server's applicationWindowService: a date stored at
// midnight UTC is a calendar day, whole in America/Denver, so a window is
// open through the end of its close day there. Any other time is an exact
// instant. The day counts and dates shown must agree with the server's
// verdict, or a faculty member sees "closed" on the last day they can apply.
const isCalendarDay = (date) => date.getTime() % DAY_MS === 0;

export function isBeforeOpen(openDate, now = new Date()) {
    const date = new Date(openDate);
    return isCalendarDay(date)
        ? universityDay(now) < date.getTime() / DAY_MS
        : now < date;
}

export function isPastClose(closeDate, now = new Date()) {
    const date = new Date(closeDate);
    return isCalendarDay(date)
        ? universityDay(now) > date.getTime() / DAY_MS
        : now > date;
}

/**
 * Whole days until a window closes: calendar days in Denver for a calendar
 * date (0 on the close day itself), rounded-up days for an exact instant.
 */
export function daysUntilClose(closeDate, now = new Date()) {
    const date = new Date(closeDate);
    return isCalendarDay(date)
        ? date.getTime() / DAY_MS - universityDay(now)
        : Math.ceil((date - now) / DAY_MS);
}

export function getDeadlineStatus(closeDate, now = new Date()) {
    const daysLeft = daysUntilClose(closeDate, now);

    let state;
    if (isPastClose(closeDate, now)) {
        state = 'closed';
    } else if (daysLeft <= 7) {
        state = 'near';
    } else {
        state = 'open';
    }

    return { state, daysLeft };
}

// Hour of day, Mountain time, at which a calendar release date takes effect.
// Must match RELEASE_HOUR in the server's decisionReleaseService.
export const DECISION_RELEASE_HOUR = 8;

const universityHourFormat = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Denver',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hourCycle: 'h23',
});

// The instant of `hour`:00 Denver time on a stored calendar day. Denver is
// UTC-6 or UTC-7, so it is whichever candidate Denver's own clock reads back
// as that day and hour; the rule of the server's universityInstant.
function universityInstant(calendarDay, hour) {
    const y = calendarDay.getUTCFullYear();
    const m = calendarDay.getUTCMonth();
    const d = calendarDay.getUTCDate();
    const candidates = [
        new Date(Date.UTC(y, m, d, hour + 6)),
        new Date(Date.UTC(y, m, d, hour + 7)),
    ];
    const match = candidates.find((candidate) => {
        const parts = {};
        for (const { type, value } of universityHourFormat.formatToParts(
            candidate
        )) {
            parts[type] = Number(value);
        }
        return (
            parts.year === y &&
            parts.month === m + 1 &&
            parts.day === d &&
            parts.hour === hour
        );
    });
    return match || candidates[0];
}

/**
 * The moment a stored decisions release date takes effect: DECISION_RELEASE_HOUR
 * in Denver on a calendar date, the exact instant for any other time, null for
 * no date. Same rule as the server's releaseMoment.
 */
export function releaseMoment(date) {
    if (!date) return null;
    const stored = new Date(date);
    return isCalendarDay(stored)
        ? universityInstant(stored, DECISION_RELEASE_HOUR)
        : stored;
}

/** The release moment in words, e.g. Monday, November 2, 2026 at 8:00 AM Mountain time. */
export function formatReleaseMoment(date) {
    const moment = releaseMoment(date);
    if (!moment) return '';
    const day = moment.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Denver',
    });
    const time = moment.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/Denver',
    });
    return `${day} at ${time} Mountain time`;
}

/**
 * An offer's response deadline in words, always Mountain time, e.g.
 * 11:59 PM Mountain time on Friday, October 30, 2026. Reads the instant the
 * server stored; the browser's own zone never enters.
 */
export function formatAnswerBy(value) {
    if (!value) return '';
    const moment = new Date(value);
    const day = moment.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Denver',
    });
    const time = moment
        .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: 'America/Denver',
        })
        .replace(/\s/g, ' ');
    return `${time} Mountain time on ${day}`;
}

/** The day alone, e.g. Oct 30, 2026, in Mountain time. */
export function formatAnswerDay(value) {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Denver',
    });
}

/** The Denver calendar day an instant falls on, as YYYY-MM-DD. */
export function denverDayOf(value) {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-CA', {
        timeZone: 'America/Denver',
    });
}

/**
 * The calendar day, as YYYY-MM-DD, that is `days` after the Denver day an
 * instant falls on. The hints a date field shows (earliest tomorrow, latest
 * 60 days out); the server's own range check decides.
 */
export function denverDayAfter(days, now = new Date()) {
    const day = new Date(universityDay(now) * DAY_MS + days * DAY_MS);
    return day.toISOString().slice(0, 10);
}

/**
 * The earliest and latest day, as YYYY-MM-DD, a nominee's answer-by date may
 * take. The server counts the window from the later of now and the program's
 * release moment, because a nominee sees the offer only at the release.
 *
 * @param {string|Date|null} [decisionsReleased] - The program's stored release date.
 */
export function nomineeAnswerByRange(
    decisionsReleased,
    latestDays,
    now = new Date()
) {
    const release = releaseMoment(decisionsReleased);
    const start = release && release > now ? release : now;
    return {
        min: denverDayAfter(1, start),
        max: denverDayAfter(latestDays, start),
    };
}

// Window dates are calendar days stored at midnight UTC; rendering in UTC
// shows the day that was entered, whatever the viewer's timezone.
export function formatWindowDate(date) {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

// A date as numeric month/day/year, e.g. 10/28/2026: a calendar day (stored at
// midnight UTC) reads as the day that was entered, an exact instant (a release
// time) as its day in Denver, whatever the viewer's timezone.
export function formatCalendarDate(value) {
    const date = new Date(value);
    return date.toLocaleDateString('en-US', {
        timeZone: isCalendarDay(date) ? 'UTC' : 'America/Denver',
    });
}

/**
 * A deadline as a day, whatever its storage: a calendar day (stored at
 * midnight UTC) reads as the day that was entered, an exact instant reads as
 * the Mountain time day it falls on. The one format for a bare deadline date,
 * e.g. Oct 30, 2026.
 */
export function formatDeadlineDay(value) {
    if (!value) return '';
    return isCalendarDay(new Date(value))
        ? formatWindowDate(value)
        : formatAnswerDay(value);
}

const daysLeftText = (n) =>
    n === 0 ? 'last day' : `${n} day${n === 1 ? '' : 's'} left`;

/**
 * The words and badge for one program's server verdict.
 *
 * @param {object} verdict - One entry of `term.applicationWindows`.
 * @param {Date} [now]
 * @returns {{ badge: string, variant: string, text: string }}
 */
export function describeProgramWindow(verdict, now = new Date()) {
    if (verdict.accepting) {
        const closeDate = verdict.extensionCloseDate || verdict.closeDate;
        if (!closeDate) {
            return {
                badge: 'Open',
                variant: 'success',
                text: 'Accepting applications',
            };
        }
        const extension = verdict.extensionCloseDate ? ', your extension' : '';
        const { state, daysLeft } = getDeadlineStatus(closeDate, now);
        if (state === 'closed') {
            // The server's verdict came with the page; the close has passed
            // since, and the submit check will refuse.
            return describeProgramWindow(
                { ...verdict, accepting: false, reason: 'closed' },
                now
            );
        }
        const open = state === 'open';
        return {
            badge: open ? 'Open' : 'Closing soon',
            variant: open ? 'success' : 'warning',
            text: `Applications close ${formatWindowDate(closeDate)}${extension}, ${daysLeftText(daysLeft)}`,
        };
    }
    if (verdict.reason === 'not_open') {
        return {
            badge: 'Not open yet',
            variant: 'secondary',
            text: `Opens ${formatWindowDate(verdict.openDate)}`,
        };
    }
    if (verdict.reason === 'closed') {
        const closedOn = verdict.extensionCloseDate || verdict.closeDate;
        return {
            badge: 'Closed',
            variant: 'danger',
            text: `Closed on ${formatWindowDate(closedOn)}`,
        };
    }
    return {
        badge: 'Not accepting',
        variant: 'secondary',
        text: 'Not accepting applications this term',
    };
}
